import { useRef, useState } from "react";
import { contactRequest } from "../lib/contactApi";
import { ContactRequestError } from "../lib/contactErrors";

const emptyForm = { name: "", email: "", topic: "", message: "", website: "" };

export const useContactForm = () => {
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState(null);
  const busy = useRef(false);
  const submission = useRef(null);

  const handleChange = (event) => {
    setForm((value) => ({ ...value, [event.target.name]: event.target.value }));
    setNotice(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (busy.current) return;
    const data = Object.fromEntries(Object.entries(form).map(([key, value]) => [key, value.trim()]));
    if (data.name.length < 2 || !data.email || data.message.length < 5 || !event.currentTarget.checkValidity()) {
      setNotice({ type: "error", key: "contact.errors.validation" });
      const invalidField = event.currentTarget.querySelector(":invalid") ||
        event.currentTarget.elements.namedItem(data.name.length < 2 ? "name" : "message");
      invalidField?.focus();
      return;
    }
    const fingerprint = JSON.stringify(data);
    if (submission.current?.fingerprint !== fingerprint) {
      submission.current = { fingerprint, requestId: crypto.randomUUID() };
    }
    busy.current = true;
    setSending(true);
    setNotice(null);
    try {
      const session = await contactRequest("/session");
      if (submission.current.csrfToken && submission.current.csrfToken !== session.csrf_token) {
        submission.current.requestId = crypto.randomUUID();
      }
      submission.current.csrfToken = session.csrf_token;
      const contact = await contactRequest("/messages", { ...data, request_id: submission.current.requestId }, session.csrf_token);
      const result = await contactRequest(`/messages/${contact.contact_id}/send`, null, session.csrf_token);
      if (result.status !== "sent") throw new ContactRequestError("unconfirmed");
      setNotice({ type: "success", key: "contact.success" });
      setForm(emptyForm);
      submission.current = null;
    } catch (error) {
      setNotice({ type: "error", key: error.messageKey || "contact.errors.unavailable" });
    } finally {
      busy.current = false;
      setSending(false);
    }
  };

  return { form, sending, notice, handleChange, handleSubmit };
};
